import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import {
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { Alert, Badge, Button, Card, LoadingState } from '../ui';
import {
  getActivityReview,
  getProjectActivityReviews,
  getProjectActivityReviewStats,
  updateActivityReview,
  updateActivityProject,
} from '../../services/activityReviewService';
import { getProjects } from '../../services/projectService';
import ChainsManager from './ChainsManager';

const REVIEW_OPTIONS = [
  {
    value: 'info_event',
    label: 'Info event',
    description: 'A useful project-related contribution. We will count it as one post or a chain automatically.',
  },
  {
    value: 'warmup',
    label: 'Warm-up',
    description: 'Natural conversation that prepares the account or audience for later work.',
  },
  {
    value: 'account_integration',
    label: 'Account setup',
    description: 'Activity used to introduce, establish, or prepare an account in a community.',
  },
  {
    value: 'other_work',
    label: 'Other project work',
    description: 'Useful project activity that does not fit the categories above.',
  },
  {
    value: 'excluded',
    label: 'Do not count',
    description: 'Unrelated, accidental, duplicated, or otherwise not part of the project result.',
  },
];

const REVIEW_LABELS = Object.fromEntries(REVIEW_OPTIONS.map((option) => [option.value, option.label]));

const getChainId = (chain) => chain?.id ?? chain?.chain_id;
const getMessageId = (message) => message?.id ?? message?.chain_message_id;

const getMembershipEvidence = (message) => {
  if (message?.membership_evidence && typeof message.membership_evidence === 'object') {
    return message.membership_evidence;
  }
  if (typeof message?.membership_evidence === 'string') {
    try {
      return JSON.parse(message.membership_evidence);
    } catch (_error) {
      return {};
    }
  }
  return {};
};

const getStableMessageKey = (message) => {
  if (message?.chat_id != null && message?.message_id != null) {
    return `telegram:${message.chat_id}:${message.message_id}`;
  }
  if (message?.observed_message_id != null) return `observed:${message.observed_message_id}`;
  return `row:${getMessageId(message)}`;
};

const getMembershipLabelRank = (message) => {
  const evidence = getMembershipEvidence(message);
  const humanRank = evidence.source === 'human_review' ? 1 : 0;
  const revisionRank = Math.max(0, Number(evidence.review_revision) || 0);
  const humanLabelRank = evidence.human_label === 'include'
    ? 900
    : evidence.human_label === 'exclude' ? 800 : 0;
  const decisionRank = humanLabelRank || ({
    excluded: 300,
    candidate: 200,
    included: 100,
  }[message?.membership_decision] || 0);
  return [
    humanRank,
    revisionRank,
    decisionRank,
    Math.max(0, Number(getMessageId(message)) || 0),
  ];
};

const hasHigherMembershipLabelRank = (left, right) => {
  const leftRank = getMembershipLabelRank(left);
  const rightRank = getMembershipLabelRank(right);
  for (let index = 0; index < leftRank.length; index += 1) {
    if (leftRank[index] !== rightRank[index]) return leftRank[index] > rightRank[index];
  }
  return false;
};

export const normalizeActivityMessages = (messages = []) => {
  const stableMessages = new Map();
  (Array.isArray(messages) ? messages : []).forEach((message) => {
    const key = getStableMessageKey(message);
    const existing = stableMessages.get(key);
    if (!existing || hasHigherMembershipLabelRank(message, existing)) {
      stableMessages.set(key, message);
    }
  });
  return Array.from(stableMessages.values()).sort((left, right) => {
    const leftTime = Date.parse(left?.sent_at);
    const rightTime = Date.parse(right?.sent_at);
    const safeLeftTime = Number.isFinite(leftTime) ? leftTime : Number.POSITIVE_INFINITY;
    const safeRightTime = Number.isFinite(rightTime) ? rightTime : Number.POSITIVE_INFINITY;
    if (safeLeftTime !== safeRightTime) return safeLeftTime - safeRightTime;
    const messageOrder = String(left?.message_id ?? '').localeCompare(
      String(right?.message_id ?? ''),
      undefined,
      { numeric: true },
    );
    if (messageOrder !== 0) return messageOrder;
    return (Number(getMessageId(left)) || 0) - (Number(getMessageId(right)) || 0);
  });
};

export const getIncludedMessageIds = (messages = []) => Array.from(new Set(
  normalizeActivityMessages(messages)
    .filter((message) => message.membership_decision === 'included')
    .map((message) => Number(getMessageId(message)))
    .filter((id) => Number.isInteger(id) && id > 0),
));

const sameMessageSelection = (left = [], right = []) => {
  if (left.length !== right.length) return false;
  const rightIds = new Set(right.map(Number));
  return left.every((id) => rightIds.has(Number(id)));
};

const formatActivityShape = (count) => {
  if (count === 1) return 'single message';
  if (count > 1) return `${count}-message chain`;
  return 'no messages selected';
};

const getCurrentKind = (chain) => {
  if (chain?.decision_status === 'rejected') return 'excluded';
  const kind = chain?.review_kind || chain?.activity_kind || chain?.kind || '';
  return REVIEW_LABELS[kind] ? kind : '';
};

const formatDate = (value) => {
  if (!value) return 'Date unavailable';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Date unavailable' : date.toLocaleString();
};

const getActivityDate = (chain) => (
  chain?.trigger_at || chain?.started_at || chain?.created_at
);

const getChatTitle = (chain) => (
  chain?.chat_title || chain?.trigger_chat_title || `Chat ${chain?.trigger_chat_id || 'unknown'}`
);

const getPreview = (chain) => (
  chain?.trigger_message_text || chain?.first_message_text || chain?.message_text || 'Open to review the activity context.'
);

const getAgentNames = (chain, messages = []) => {
  const suppliedAgents = chain?.agents || chain?.agent_usernames || [];
  const suppliedNames = suppliedAgents.map((agent) => {
    if (typeof agent === 'string') return agent;
    return agent.username || agent.name || agent.first_name;
  });
  const messageNames = messages.map((message) => (
    message.username || message.agent_username || message.first_name
  ));
  return [...new Set([...suppliedNames, ...messageNames].filter(Boolean))];
};

const isContextMessage = (chain, message) => {
  if (!chain || !message) return false;

  if (
    chain.detection_source === 'standalone_agent_message'
    && chain.source_observed_message_id != null
    && message.observed_message_id != null
    && String(chain.source_observed_message_id) === String(message.observed_message_id)
  ) {
    return true;
  }

  return (
    chain.trigger_chat_id != null
    && chain.trigger_message_id != null
    && message.chat_id != null
    && message.message_id != null
    && String(chain.trigger_chat_id) === String(message.chat_id)
    && String(chain.trigger_message_id) === String(message.message_id)
  );
};

export const describeDetectionSource = (source) => {
  const normalized = String(source || '').toLowerCase();
  if (normalized.includes('reply')) {
    return 'An agent replied in a tracked conversation, so this may be project activity.';
  }
  if (normalized.includes('keyword')) {
    return 'The conversation matched a project topic, so it needs a quick human check.';
  }
  if (normalized.includes('time') || normalized.includes('topic') || normalized.includes('similar')) {
    return 'Nearby messages appear to belong to the same topic, but a person should confirm the connection.';
  }
  if (normalized.includes('manual')) {
    return 'This activity was sent for a human check.';
  }
  return 'The system found possible project activity and needs a quick human decision.';
};

const ProjectAssignmentCard = ({ chain, projects, onSave, saving, error }) => {
  const [editing, setEditing] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const assignedProjectName = chain.assigned_project_name || chain.project_name || 'No project';
  const isHumanConfirmed = chain.assignment_source === 'human';

  useEffect(() => {
    setEditing(false);
    setSelectedProjectId(chain.project_id ? String(chain.project_id) : '');
  }, [chain.id, chain.project_id]);

  const submitProject = (value) => {
    const projectId = value === 'unassigned' ? null : Number(value);
    onSave(projectId);
  };

  return (
    <section className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4" aria-labelledby="activity-project-heading">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h4 id="activity-project-heading" className="text-sm font-semibold text-gray-900">Project</h4>
          <p className="mt-1 text-base font-medium text-gray-900">{assignedProjectName}</p>
          {chain.assignment_reason_summary && (
            <p className="mt-1 max-w-2xl text-sm text-gray-600">{chain.assignment_reason_summary}</p>
          )}
        </div>
        <Badge variant={isHumanConfirmed ? 'green' : 'blue'}>
          {isHumanConfirmed ? 'Project confirmed' : 'Suggested by AI'}
        </Badge>
      </div>

      {editing ? (
        <div className="mt-4 flex flex-col gap-3 sm:flex-row">
          <label className="min-w-0 flex-1">
            <span className="sr-only">Choose another active project</span>
            <select
              value={selectedProjectId}
              onChange={(event) => setSelectedProjectId(event.target.value)}
              className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Choose an active project</option>
              {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
              <option value="unassigned">Not enough information — leave unassigned</option>
            </select>
          </label>
          <Button variant="secondary" className="min-h-[44px]" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          <Button
            className="min-h-[44px]"
            onClick={() => submitProject(selectedProjectId)}
            disabled={!selectedProjectId || saving}
            isLoading={saving}
          >
            Save project
          </Button>
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="secondary" className="min-h-[44px]" onClick={() => setEditing(true)} disabled={saving}>
            Change project
          </Button>
          {!isHumanConfirmed && chain.project_id && (
            <Button className="min-h-[44px]" onClick={() => onSave(Number(chain.project_id))} disabled={saving} isLoading={saving}>
              Project is correct
            </Button>
          )}
        </div>
      )}
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
    </section>
  );
};

const ActivityListItem = ({ chain, isSelected, onSelect, reviewed }) => {
  const chainId = getChainId(chain);
  const includedCount = Number(chain.message_count ?? 0);
  const capturedCount = Number(chain.total_agent_messages ?? includedCount);
  return (
    <button
      type="button"
      onClick={() => onSelect(chainId)}
      aria-pressed={isSelected}
      className={`block w-full min-h-[44px] px-4 py-4 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
        isSelected ? 'bg-primary-50 border-l-4 border-primary-500' : 'hover:bg-gray-50 border-l-4 border-transparent'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{getChatTitle(chain)}</p>
          <p className="mt-1 line-clamp-2 text-sm text-gray-600">{getPreview(chain)}</p>
        </div>
        {reviewed && getCurrentKind(chain) && (
          <Badge variant={getCurrentKind(chain) === 'excluded' ? 'gray' : 'green'} size="sm">
            {REVIEW_LABELS[getCurrentKind(chain)] || 'Reviewed'}
          </Badge>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        <span>{formatDate(getActivityDate(chain))}</span>
        <span>{includedCount} included · {capturedCount} captured</span>
      </div>
    </button>
  );
};

const MessageCard = ({
  message,
  context = false,
  selectable = false,
  included = true,
  membershipDecision = 'candidate',
  onToggle,
  disabled = false,
}) => {
  const name = context
    ? (message.trigger_sender_username || message.sender_username || 'Conversation context')
    : (message.username || message.agent_username || message.first_name || 'Agent');
  const text = context
    ? (message.trigger_message_text || message.message_text)
    : (message.message_text || message.text);
  const sentAt = context
    ? (message.trigger_at || message.created_at)
    : (message.sent_at || message.created_at);
  const visibleText = text || (context
    ? 'The original conversation text was not stored.'
    : 'This message has no stored text or caption.');

  const accessibleLabel = `${included ? 'Remove' : 'Include'} message from ${name}: ${String(visibleText).slice(0, 80)}`;
  const membershipLabel = context
    ? 'Context · not counted'
    : included
      ? 'Included in result'
      : membershipDecision === 'candidate'
        ? 'Candidate · not counted'
        : 'Excluded from result';

  return (
    <div className={`rounded-lg border p-3 ${
      context
        ? 'border-gray-200 bg-gray-50'
        : included
          ? 'border-blue-100 bg-blue-50'
          : 'border-gray-200 bg-gray-50 opacity-70'
    }`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium text-gray-900">
          {context ? name : (String(name).startsWith('@') ? name : `@${name}`)}
        </p>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <span className="text-xs font-medium text-gray-600">{membershipLabel}</span>
          <p className="text-xs text-gray-500">{formatDate(sentAt)}</p>
        </div>
      </div>
      <p className="mt-2 whitespace-pre-wrap break-words text-sm text-gray-700">{visibleText}</p>
      {selectable && !context && (
        <label className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-800 focus-within:ring-2 focus-within:ring-primary-500">
          <input
            type="checkbox"
            checked={included}
            onChange={() => onToggle?.(getMessageId(message))}
            disabled={disabled}
            aria-label={accessibleLabel}
            className="h-5 w-5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          {included ? 'Keep in this result' : 'Add back to this result'}
        </label>
      )}
    </div>
  );
};

const ActivityReviewDetail = ({
  chain,
  messages,
  includedMessageIds,
  onToggleMessage,
  selectedKind,
  onKindChange,
  onSave,
  onSkip,
  saving,
  statusMessage,
  saveError,
  reviewed,
  dirty,
  projects,
  onSaveProject,
  projectSaving,
  projectError,
}) => {
  const [compositionOpen, setCompositionOpen] = useState(false);
  const includedIds = new Set(includedMessageIds.map(Number));
  const includedMessages = messages.filter((message) => (
    includedIds.has(Number(getMessageId(message)))
  ));
  const agents = getAgentNames(chain, includedMessages);
  const count = includedMessageIds.length;
  const shapeLabel = formatActivityShape(count);
  const headingRef = useRef(null);
  const visibleContext = !messages.some((message) => isContextMessage(chain, message));
  const showConversationContext = visibleContext
    && chain.detection_source !== 'standalone_agent_message';

  useEffect(() => {
    headingRef.current?.focus();
    setCompositionOpen(false);
  }, [chain.id, chain.chain_id]);

  const saveLabel = selectedKind === 'excluded'
    ? 'Do not count and show next'
    : selectedKind
      ? `${reviewed ? 'Save' : 'Confirm'}: ${REVIEW_LABELS[selectedKind]} · ${shapeLabel}`
      : reviewed ? 'Save changes' : 'Choose a result';

  return (
    <Card className="lg:sticky lg:top-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 ref={headingRef} tabIndex="-1" className="text-lg font-semibold text-gray-900 focus:outline-none">{getChatTitle(chain)}</h3>
          <p className="mt-1 text-sm text-gray-500">{formatDate(getActivityDate(chain))}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {dirty && <Badge variant="yellow">Not saved</Badge>}
          <Badge variant="blue">{reviewed ? 'Reviewed' : 'Candidate'} · {shapeLabel}</Badge>
        </div>
      </div>

      <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
        <div className="flex items-start gap-2 text-gray-600">
          <UserGroupIcon className="mt-0.5 h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
          <div>
            <dt className="font-medium text-gray-900">Agents</dt>
            <dd>{agents.length ? agents.map((name) => `@${String(name).replace(/^@/, '')}`).join(', ') : 'Not recorded'}</dd>
          </div>
        </div>
        <div className="flex items-start gap-2 text-gray-600">
          <ChatBubbleLeftRightIcon className="mt-0.5 h-5 w-5 flex-none text-gray-400" aria-hidden="true" />
          <div>
            <dt className="font-medium text-gray-900">Activity size</dt>
            <dd>{count} included agent message{count === 1 ? '' : 's'}</dd>
          </div>
        </div>
      </dl>

      <Alert variant="info" title="Why is this here?" className="mt-5">
        {describeDetectionSource(chain.detection_source)}
      </Alert>

      {chain.assignment_scope === 'global' && (
        <ProjectAssignmentCard
          chain={chain}
          projects={projects}
          onSave={onSaveProject}
          saving={projectSaving}
          error={projectError}
        />
      )}

      <section className="mt-6" aria-labelledby="activity-context-heading">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 id="activity-context-heading" className="text-sm font-semibold text-gray-900">Messages and context</h4>
            <p className="mt-1 text-sm text-gray-500">The shape is calculated automatically from the messages you keep.</p>
          </div>
          {messages.length > 0 && (
            <Button
              type="button"
              variant="secondary"
              className="min-h-[44px]"
              onClick={() => setCompositionOpen((open) => !open)}
              aria-expanded={compositionOpen}
            >
              {compositionOpen ? 'Done adjusting' : `Adjust messages · ${count} included`}
            </Button>
          )}
        </div>
        <div
          className="mt-3 max-h-80 space-y-3 overflow-y-auto pr-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
          role="region"
          aria-label="Activity messages and conversation context"
          tabIndex="0"
        >
          {showConversationContext && <MessageCard message={chain} context />}
          {messages.map((message, index) => (
            <MessageCard
              key={message.id || message.message_id || index}
              message={message}
              included={includedIds.has(Number(getMessageId(message)))}
              membershipDecision={message.membership_decision}
              selectable={compositionOpen}
              onToggle={onToggleMessage}
              disabled={saving}
            />
          ))}
          {messages.length === 0 && (
            <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-500">No agent message was stored for this activity.</p>
          )}
        </div>
      </section>

      {count === 0 && (
        <Alert variant="error" title="Keep at least one message" className="mt-5">
          Every review needs its source message. Keep the closest message selected, then choose “Do not count” if the activity is not project work.
        </Alert>
      )}

      <fieldset className="mt-6" disabled={saving}>
        <legend className="text-base font-semibold text-gray-900">How should this activity be counted?</legend>
        <p className="mt-1 text-sm text-gray-500">Choose the closest result. You can change a reviewed activity later.</p>
        <div className="mt-3 space-y-2" role="radiogroup" aria-label="Activity classification">
          {REVIEW_OPTIONS.map((option) => {
            const checked = selectedKind === option.value;
            return (
              <label
                key={option.value}
                className={`flex min-h-[64px] cursor-pointer items-start gap-3 rounded-lg border p-3 focus-within:ring-2 focus-within:ring-primary-500 ${
                  checked ? 'border-primary-500 bg-primary-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                } ${saving ? 'cursor-not-allowed opacity-60' : ''}`}
              >
                <input
                  type="radio"
                  name="activity-review-kind"
                  value={option.value}
                  checked={checked}
                  onChange={(event) => onKindChange(event.target.value)}
                  className="mt-1 h-5 w-5 border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{option.label}</span>
                  <span className="mt-0.5 block text-sm text-gray-600">{option.description}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {saveError && (
        <Alert variant="error" title="Activity was not saved" className="mt-5">
          {saveError}
        </Alert>
      )}

      <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          variant="secondary"
          size="lg"
          className="min-h-[44px]"
          onClick={onSkip}
          disabled={saving}
        >
          Skip for now
        </Button>
        <Button
          size="lg"
          className="min-h-[44px]"
          onClick={onSave}
          disabled={!selectedKind || saving || count === 0}
          isLoading={saving}
        >
          {saveLabel}
        </Button>
      </div>
      <p className="sr-only" aria-live="polite">{statusMessage}</p>
    </Card>
  );
};

const ActivityReviewQueue = ({ projectId }) => {
  const [view, setView] = useState('pending');
  const [selectedId, setSelectedId] = useState(null);
  const [selectedKind, setSelectedKind] = useState('');
  const [includedMessageIds, setIncludedMessageIds] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');
  const [saveError, setSaveError] = useState('');
  const [projectError, setProjectError] = useState('');
  const initializedReviewRef = useRef(null);
  const queryClient = useQueryClient();

  const statsQuery = useQuery(
    ['activityReviewStats', projectId],
    () => getProjectActivityReviewStats(projectId),
    { enabled: Boolean(projectId) },
  );

  const projectsQuery = useQuery(
    ['activeProjectsForActivityAssignment'],
    getProjects,
    {
      select: (projects) => (Array.isArray(projects) ? projects.filter((project) => project.is_active !== false) : []),
    },
  );

  const reviewsQuery = useQuery(
    ['activityReviews', projectId, view],
    () => getProjectActivityReviews(projectId, { status: view, limit: 100, offset: 0 }),
    { enabled: Boolean(projectId) && view !== 'all' },
  );

  const reviews = useMemo(() => reviewsQuery.data?.reviews || [], [reviewsQuery.data]);
  const currentProjectName = (projectsQuery.data || []).find(
    (project) => Number(project.id) === Number(projectId),
  )?.name;
  const pendingCount = Number(statsQuery.data?.pending ?? (
    view === 'pending' ? reviewsQuery.data?.remaining ?? reviewsQuery.data?.total : 0
  ) ?? 0);

  useEffect(() => {
    if (view === 'all') return;
    const selectionStillExists = reviews.some((review) => getChainId(review) === selectedId);
    if (!selectionStillExists) {
      setSelectedId(reviews.length ? getChainId(reviews[0]) : null);
    }
  }, [reviews, selectedId, view]);

  const detailQuery = useQuery(
    ['activityReview', selectedId],
    () => getActivityReview(selectedId),
    { enabled: selectedId !== null && selectedId !== undefined && view !== 'all' },
  );

  const selectedSummary = reviews.find((review) => getChainId(review) === selectedId);
  const selectedChain = detailQuery.data?.chain
    ? { ...(selectedSummary || {}), ...detailQuery.data.chain }
    : selectedSummary;
  const messages = useMemo(
    () => normalizeActivityMessages(detailQuery.data?.messages || []),
    [detailQuery.data?.messages],
  );
  const currentSelectedKind = getCurrentKind(selectedChain);
  const loadedReviewKey = detailQuery.data?.chain
    ? `${getChainId(detailQuery.data.chain)}:${detailQuery.data.chain.review_revision}`
    : null;
  const initialIncludedMessageIds = useMemo(() => getIncludedMessageIds(messages), [messages]);
  const dirty = Boolean(selectedChain) && (
    selectedKind !== currentSelectedKind
    || !sameMessageSelection(includedMessageIds, initialIncludedMessageIds)
  );

  useEffect(() => {
    if (!loadedReviewKey || initializedReviewRef.current === loadedReviewKey) return;
    initializedReviewRef.current = loadedReviewKey;
    setSelectedKind(currentSelectedKind);
    setIncludedMessageIds(getIncludedMessageIds(messages));
    setStatusMessage('');
    setSaveError('');
    setProjectError('');
  }, [loadedReviewKey, currentSelectedKind, messages]);

  const saveMutation = useMutation(
    () => updateActivityReview(selectedId, {
      kind: selectedKind,
      revision: Number(selectedChain?.review_revision || 0),
      includedMessageIds,
    }),
    {
      onMutate: () => {
        setSaveError('');
      },
      onSuccess: (data) => {
        const currentIndex = reviews.findIndex((review) => getChainId(review) === selectedId);
        const nextReview = reviews[currentIndex + 1] || reviews[currentIndex - 1];
        queryClient.setQueryData(['activityReview', selectedId], (current) => ({
          ...(current || {}),
          chain: data?.chain || current?.chain,
          messages: (current?.messages || []).map((message) => {
            const membership = (data?.memberships || []).find(
              (item) => Number(item.id) === Number(getMessageId(message)),
            );
            return membership ? { ...message, ...membership } : message;
          }),
        }));
        queryClient.invalidateQueries(['activityReviews', projectId]);
        queryClient.invalidateQueries(['activityReviewStats', projectId]);
        queryClient.invalidateQueries(['activityProjectAssignmentQuality']);
        setStatusMessage('Activity saved.');
        setSaveError('');
        toast.success('Activity saved.');
        if (view === 'pending') {
          queryClient.setQueryData(['activityReviews', projectId, view], (current) => {
            if (!current) return current;
            return {
              ...current,
              reviews: (current.reviews || []).filter((review) => getChainId(review) !== selectedId),
              total: Math.max(0, Number(current.total || 0) - 1),
              remaining: Math.max(0, Number(current.remaining || 0) - 1),
            };
          });
          setSelectedId(nextReview ? getChainId(nextReview) : null);
        }
      },
      onError: async (error) => {
        if (error.response?.status === 409) {
          await Promise.all([
            queryClient.refetchQueries(['activityReview', selectedId]),
            queryClient.refetchQueries(['activityReviews', projectId, view]),
          ]);
          const conflictMessage = 'This activity changed while you were reviewing it. The latest queue state is shown; please review the current item again.';
          setStatusMessage(conflictMessage);
          toast.info(conflictMessage);
          return;
        }
        const message = 'Your selection is still here. Check your connection and try saving again.';
        setSaveError(message);
        setStatusMessage(`The activity could not be saved. ${message}`);
        toast.error('Activity could not be saved. Please try again.');
      },
    },
  );

  const projectMutation = useMutation(
    (nextProjectId) => updateActivityProject(selectedId, {
      projectId: nextProjectId,
      revision: Number(selectedChain?.assignment_revision || 0),
    }),
    {
      onMutate: () => setProjectError(''),
      onSuccess: (data, nextProjectId) => {
        const movedAway = Number(nextProjectId) !== Number(projectId);
        const projectName = (projectsQuery.data || []).find(
          (project) => Number(project.id) === Number(nextProjectId),
        )?.name;
        queryClient.setQueryData(['activityReview', selectedId], (current) => ({
          ...(current || {}),
          chain: { ...(current?.chain || {}), ...(data?.chain || {}) },
        }));
        queryClient.invalidateQueries(['activityReviews']);
        queryClient.invalidateQueries(['activityReviewStats']);
        queryClient.invalidateQueries(['unassignedActivityReviews']);
        queryClient.invalidateQueries(['unassignedActivityReviewCount']);
        queryClient.invalidateQueries(['agentChains']);
        queryClient.invalidateQueries(['activityProjectAssignmentQuality']);
        toast.success(nextProjectId ? `Activity moved to ${projectName || 'the selected project'}.` : 'Activity moved to Needs project.');
        setStatusMessage('Project assignment saved.');
        if (movedAway) {
          setSelectedId(null);
        }
      },
      onError: async (error) => {
        if (error.response?.status === 409) {
          await Promise.all([
            queryClient.refetchQueries(['activityReview', selectedId]),
            queryClient.refetchQueries(['activityReviews', projectId, view]),
          ]);
          toast.info('This project assignment changed. The latest version is shown.');
          return;
        }
        setProjectError('Project was not saved. Your selection is still here.');
        toast.error('Project was not saved.');
      },
    },
  );

  const confirmDiscardDraft = () => (
    !dirty
    || window.confirm('This activity has unsaved changes. Leave without saving them?')
  );

  const selectActivity = (chainId) => {
    if (chainId === selectedId) return;
    if (!confirmDiscardDraft()) return;
    setSelectedId(chainId);
  };

  const saveProjectAssignment = (nextProjectId) => {
    if (!confirmDiscardDraft()) return;
    projectMutation.mutate(nextProjectId);
  };

  const toggleMessage = (messageId) => {
    const normalizedId = Number(messageId);
    if (!Number.isInteger(normalizedId) || normalizedId < 1) return;
    setIncludedMessageIds((current) => (
      current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...current, normalizedId]
    ));
    setSaveError('');
  };

  const handleSkip = () => {
    if (reviews.length < 2) {
      setStatusMessage('There are no other activities in this list right now.');
      return;
    }
    if (!confirmDiscardDraft()) return;
    const currentIndex = reviews.findIndex((review) => getChainId(review) === selectedId);
    const nextReview = reviews[(currentIndex + 1) % reviews.length];
    setSelectedId(getChainId(nextReview));
    setStatusMessage('Activity skipped for now.');
  };

  const changeView = (nextView) => {
    if (nextView === view || !confirmDiscardDraft()) return;
    initializedReviewRef.current = null;
    setView(nextView);
    setSelectedId(null);
    setStatusMessage('');
    setSaveError('');
  };

  if (view === 'all') {
    return (
      <div className="mt-4 space-y-4">
        <ReviewHeader view={view} pendingCount={pendingCount} projectName={currentProjectName} onChangeView={changeView} />
        <ChainsManager projectId={projectId} />
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <ReviewHeader view={view} pendingCount={pendingCount} projectName={currentProjectName} onChangeView={changeView} />

      {reviewsQuery.isLoading ? (
        <LoadingState message={`Loading ${view === 'pending' ? 'activities to review' : 'reviewed activities'}...`} />
      ) : reviewsQuery.isError ? (
        <Alert variant="error" title="Activity review could not be loaded">
          <p>Try again. Nothing has been changed.</p>
          <Button variant="secondary" className="mt-3 min-h-[44px]" icon={ArrowPathIcon} onClick={() => reviewsQuery.refetch()}>
            Try again
          </Button>
        </Alert>
      ) : reviews.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" aria-hidden="true" />
            <h3 className="mt-3 text-lg font-semibold text-gray-900">
              {view === 'pending' ? "You're all caught up" : 'No reviewed activities yet'}
            </h3>
            <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
              {view === 'pending'
                ? 'There are no activities waiting for a decision.'
                : 'Activities will appear here after someone reviews them.'}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(280px,0.8fr)_minmax(0,1.5fr)]">
          <div className="order-1 lg:order-2">
            {detailQuery.isLoading || !selectedChain ? (
              <Card><LoadingState message="Loading activity details..." /></Card>
            ) : detailQuery.isError ? (
              <Alert variant="error" title="Activity details could not be loaded">
                <p>The activity remains in the list. Try loading its details again.</p>
                <Button variant="secondary" className="mt-3 min-h-[44px]" onClick={() => detailQuery.refetch()}>
                  Try again
                </Button>
              </Alert>
            ) : (
              <ActivityReviewDetail
                chain={selectedChain}
                messages={messages}
                includedMessageIds={includedMessageIds}
                onToggleMessage={toggleMessage}
                selectedKind={selectedKind}
                onKindChange={(kind) => {
                  setSelectedKind(kind);
                  setSaveError('');
                }}
                onSave={() => saveMutation.mutate()}
                onSkip={handleSkip}
                saving={saveMutation.isLoading}
                statusMessage={statusMessage}
                saveError={saveError}
                reviewed={view === 'reviewed'}
                dirty={dirty}
                projects={projectsQuery.data || []}
                onSaveProject={saveProjectAssignment}
                projectSaving={projectMutation.isLoading}
                projectError={projectError}
              />
            )}
          </div>

          <Card className="order-2 overflow-hidden lg:order-1">
            <div className="-m-4 divide-y divide-gray-100 sm:-m-6" aria-label={view === 'pending' ? 'Activities to review' : 'Reviewed activities'}>
              {reviews.map((review) => (
                <ActivityListItem
                  key={getChainId(review)}
                  chain={review}
                  isSelected={getChainId(review) === selectedId}
                  onSelect={selectActivity}
                  reviewed={view === 'reviewed'}
                />
              ))}
            </div>
          </Card>
        </div>
      )}
      <div className="sr-only" aria-live="polite">{statusMessage}</div>
    </div>
  );
};

const ReviewHeader = ({ view, pendingCount, projectName, onChangeView }) => (
  <div className="space-y-4">
    <Alert variant="info" title="A quick check keeps reports accurate">
      Only completed chains assigned to {projectName ? `“${projectName}”` : 'this project'} are shown here. Check the project, messages, and result before confirming it.
    </Alert>
    <div className="overflow-x-auto border-b border-gray-200">
      <nav className="-mb-px flex min-w-max gap-2" aria-label="Activity review views">
        {[
          ['pending', `To review · ${pendingCount}`],
          ['reviewed', 'Reviewed'],
          ['all', 'All chains'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => onChangeView(value)}
            aria-current={view === value ? 'page' : undefined}
            className={`min-h-[44px] whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 ${
              view === value
                ? 'border-primary-500 text-primary-700'
                : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'
            }`}
          >
            {label}
          </button>
        ))}
      </nav>
    </div>
  </div>
);

export default ActivityReviewQueue;
